import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
    Loader2, 
    ArrowLeft,
    Save,
    Star,
    DollarSign,
    ThumbsUp,
    ThumbsDown,
    AlertTriangle,
    CheckCircle,
    RefreshCw
} from 'lucide-react';
import { reviewAPI } from '../lib/api';
import { formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';

// Star Rating Component
const StarRating = ({ value, onChange, label }) => {
    const [hovered, setHovered] = useState(0);
    
    return (
        <div className="space-y-2">
            <Label className="text-sm font-medium">{label}</Label>
            <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        type="button"
                        onClick={() => onChange(star)}
                        onMouseEnter={() => setHovered(star)}
                        onMouseLeave={() => setHovered(0)}
                        className="p-0.5 transition-transform hover:scale-110 active:scale-95"
                    >
                        <Star 
                            className={cn(
                                "h-7 w-7 transition-colors",
                                star <= (hovered || value) 
                                    ? 'fill-amber-400 text-amber-400' 
                                    : 'text-muted-foreground/30'
                            )} 
                        />
                    </button>
                ))}
            </div>
        </div>
    );
};

const QuickReviewUpdate = () => {
    const { reviewId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [existingData, setExistingData] = useState(null);
    const [formData, setFormData] = useState({
        payment_received: true,
        would_work_again: true,
        require_deposit_next_time: false,
        decline_future_work: false,
        notes: '',
        ratings: {}
    });

    useEffect(() => {
        loadExistingReview();
    }, [reviewId]);

    const loadExistingReview = async () => {
        try {
            // We need to get the review data - use my reviews endpoint
            const response = await reviewAPI.getMyReviews();
            const review = response.data.find(r => r.id === reviewId);
            
            if (!review) {
                toast.error('Review not found');
                navigate('/dashboard');
                return;
            }

            setExistingData(review);
            setFormData({
                payment_received: review.payment_received ?? true,
                would_work_again: review.would_work_again ?? true,
                require_deposit_next_time: review.require_deposit_next_time ?? false,
                decline_future_work: review.decline_future_work ?? false,
                notes: review.notes || '',
                ratings: review.ratings || {}
            });
        } catch (err) {
            console.error('Failed to load review:', err);
            toast.error('Failed to load review');
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            await reviewAPI.quickUpdate(reviewId, formData);
            toast.success('Review updated successfully!');
            
            // Navigate back to the property page
            const addressId = existingData?.address_id || existingData?.property_id;
            if (addressId) {
                navigate(`/property/${addressId}`);
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            console.error('Failed to update review:', err);
            toast.error(err.response?.data?.detail || 'Failed to update review');
        } finally {
            setSaving(false);
        }
    };

    const updateRating = (key, value) => {
        setFormData(prev => ({
            ...prev,
            ratings: { ...prev.ratings, [key]: value }
        }));
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-lg mx-auto px-4 py-6">
                {/* Back Button */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(-1)}
                    className="mb-4 -ml-2"
                    data-testid="back-btn"
                >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                </Button>

                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <RefreshCw className="h-5 w-5 text-primary" />
                            Update Your Review
                        </CardTitle>
                        <CardDescription>
                            Update the outcome or payment status for your previous review
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {/* Original Review Info */}
                        <div className="mb-6 p-4 bg-accent/30 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-1">Original Review</p>
                            <p className="font-medium text-foreground">
                                {existingData?.address_street}, {existingData?.address_city}
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {existingData?.job_type} • {existingData?.project_size} • {formatDate(existingData?.created_at)}
                            </p>
                        </div>

                        {/* Disclaimer Banner */}
                        <div className="mb-6 p-3 bg-primary/5 border border-primary/20 rounded-lg" data-testid="review-disclaimer-banner">
                            <p className="text-xs text-muted-foreground text-center leading-relaxed">
                                Your ratings represent your personal perception and opinion of your job experience. 
                                Do not include factual claims, names, or identifying information about any individual.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Payment Status */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-foreground flex items-center gap-2">
                                    <DollarSign className="h-4 w-4" />
                                    Payment Status
                                </h3>
                                <div className="flex items-center justify-between p-4 bg-accent/30 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        {formData.payment_received ? (
                                            <CheckCircle className="h-5 w-5 text-emerald-500" />
                                        ) : (
                                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                                        )}
                                        <div>
                                            <Label className="text-base">Payment Received</Label>
                                            <p className="text-xs text-muted-foreground">
                                                {formData.payment_received ? 'You received full payment' : 'Payment is outstanding'}
                                            </p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={formData.payment_received}
                                        onCheckedChange={(checked) => setFormData({...formData, payment_received: checked})}
                                        data-testid="payment-received-switch"
                                    />
                                </div>
                            </div>

                            {/* Would Work Again */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-foreground flex items-center gap-2">
                                    <ThumbsUp className="h-4 w-4" />
                                    Future Work
                                </h3>
                                <div className="flex items-center justify-between p-4 bg-accent/30 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        {formData.would_work_again ? (
                                            <ThumbsUp className="h-5 w-5 text-emerald-500" />
                                        ) : (
                                            <ThumbsDown className="h-5 w-5 text-red-500" />
                                        )}
                                        <div>
                                            <Label className="text-base">Would Work Again</Label>
                                            <p className="text-xs text-muted-foreground">
                                                {formData.would_work_again ? 'You would take another job here' : 'You would not take another job here'}
                                            </p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={formData.would_work_again}
                                        onCheckedChange={(checked) => setFormData({...formData, would_work_again: checked})}
                                        data-testid="would-work-again-switch"
                                    />
                                </div>

                                <div className="flex items-center justify-between p-4 bg-accent/30 rounded-lg">
                                    <div>
                                        <Label className="text-base">Require Deposit Next Time</Label>
                                        <p className="text-xs text-muted-foreground">
                                            Would you require upfront payment in the future?
                                        </p>
                                    </div>
                                    <Switch
                                        checked={formData.require_deposit_next_time}
                                        onCheckedChange={(checked) => setFormData({...formData, require_deposit_next_time: checked})}
                                        data-testid="require-deposit-switch"
                                    />
                                </div>

                                <div className="flex items-center justify-between p-4 bg-accent/30 rounded-lg">
                                    <div>
                                        <Label className="text-base">Decline Future Work</Label>
                                        <p className="text-xs text-muted-foreground">
                                            Would you decline to work at this address again?
                                        </p>
                                    </div>
                                    <Switch
                                        checked={formData.decline_future_work}
                                        onCheckedChange={(checked) => setFormData({...formData, decline_future_work: checked})}
                                        data-testid="decline-future-switch"
                                    />
                                </div>
                            </div>

                            {/* Ratings Update */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-foreground flex items-center gap-2">
                                    <Star className="h-4 w-4" />
                                    Update Ratings (Optional)
                                </h3>
                                <div className="grid grid-cols-1 gap-4">
                                    <StarRating 
                                        label="My payment experience"
                                        value={formData.ratings.payment_timeliness || 3}
                                        onChange={(v) => updateRating('payment_timeliness', v)}
                                    />
                                    <StarRating 
                                        label="My communication experience"
                                        value={formData.ratings.communication || 3}
                                        onChange={(v) => updateRating('communication', v)}
                                    />
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="space-y-2">
                                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                                <Textarea
                                    id="notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                    placeholder="Any updates to your experience..."
                                    className="min-h-[100px] bg-accent border-border"
                                    maxLength={250}
                                    data-testid="notes-input"
                                />
                                <p className="text-xs text-muted-foreground text-right">
                                    {formData.notes.length}/250
                                </p>
                            </div>

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                className="w-full h-12"
                                disabled={saving}
                                data-testid="save-update-btn"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="mr-2 h-4 w-4" />
                                        Save Update
                                    </>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
};

export default QuickReviewUpdate;
